import { INITIAL_AUTH_GATE_TIMEOUT_MS } from '@/constants';
import type { AuthData } from '@/types/auth';
import { useEffect, useState } from 'react';

type InitialAuthGateState = 'checking' | 'resolved' | 'timed-out';

type InitialAuthGateOptions = {
  // False on public routes, where /auth/me is disabled and AUTHENTICATED is unreachable. The root
  // component survives client-side navigation, so a watchdog armed while the gate was inapplicable
  // would fire instantly on the first protected route reached.
  enabled?: boolean;
  timeoutMs?: number;
};

// `checking` until the first AUTHENTICATED result — see POR-433 / SESSION_EXPIRATION_FIX.md's
// "Cold-Load Race". Latches `resolved` after, so a later PENDING/UNAUTHENTICATED never re-gates;
// `timed-out` is the watchdog's escape hatch, since that latch would otherwise never open.
function useInitialAuthGate(
  status: AuthData['status'],
  { enabled = true, timeoutMs = INITIAL_AUTH_GATE_TIMEOUT_MS }: InitialAuthGateOptions = {},
): InitialAuthGateState {
  const [hasAuthenticated, setHasAuthenticated] = useState(status === 'AUTHENTICATED');
  const [timedOut, setTimedOut] = useState(false);

  // Render-phase state update rather than a ref write: a concurrent render that React discards
  // must not latch the gate open, which would silently defeat it (POR-433 review finding).
  if (status === 'AUTHENTICATED' && !hasAuthenticated) {
    setHasAuthenticated(true);
  }

  const gating = enabled && !hasAuthenticated;

  useEffect(() => {
    if (!gating) {
      // oxlint-disable-next-line set-state-in-effect -- resets the timer-driven latch so the next gating cycle starts fresh (POR-433)
      setTimedOut(false);
      return undefined;
    }

    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [gating, timeoutMs]);

  if (!gating) return 'resolved';
  return timedOut ? 'timed-out' : 'checking';
}

export { useInitialAuthGate };
export type { InitialAuthGateState };
